import { cloneDeep, forEach, isEmpty } from 'lodash';
import queryString from 'query-string';
import { push } from 'react-router-redux';
import { CoordinatorService, JobService } from '@services';
import { apiErrorHandler } from '@config/utils';
import utils from '@utils';
import Actions from '.';

const COORDINATORS_FILTERS = ['pageNumber'];

/* ------------- Thunks actions ------------- */
const fetchPendingInvitations = () => ((dispatch) => {
	CoordinatorService.getPendingInvitations()
		.then(({ data }) => {
			dispatch(Actions.updateStoreProperties({
				pendingInvitation : data,
			}));
			dispatch(Actions.setLoading('list', false));
		})
		.catch(apiErrorHandler({
			dispatch,
			module   : 'coordinators',
			property : 'loading.list',
		}));
});

const getCoordinatorsData = () => ((dispatch, getState) => {
	const { filters } = getState().coordinators;
	const { pageNumber, displayCount } = filters;
	const newFilter = cloneDeep(filters);
	dispatch(Actions.setLoading('list', true));
	CoordinatorService.getAll({ pageNumber, displayCount })
		.then(({ data : { Count, ItemList } }) => {
			newFilter.totalCount = Count;
			dispatch(Actions.updateStoreProperties({
				filters : newFilter,
				data    : ItemList,
			}));
			dispatch(fetchPendingInvitations());
		})
		.catch(apiErrorHandler({
			dispatch,
			module   : 'coordinators',
			property : 'loading.list',
		}));
});

const getDetailsData = (Id) => ((dispatch) => {
	dispatch(Actions.setLoading('details', true));
	CoordinatorService.getById(Id)
		.then(({ data }) => {
			dispatch(Actions.setLoading('details', false));
			dispatch(Actions.updateDetailsProperties({ data }));
		})
		.catch(apiErrorHandler({
			dispatch,
			module   : 'coordinators',
			property : 'loading.details',
		}));
});

const getJobsData = (section = 'jobHistory') => ((dispatch, getState) => {
	const {
		match : { params : { id : agencyId } },
	} = getState().router;
	const {
		details : {
			activeJobs,
			jobHistory,
		},
	} = getState().coordinators;
	let pageNumber = 1;
	let displayCount = 25;

	if (section === 'activeJobs') {
		const { filters } = activeJobs;
		pageNumber = filters.pageNumber;
		displayCount = filters.displayCount;
	} else {
		const { filters } = jobHistory;
		pageNumber = filters.pageNumber;
		displayCount = filters.displayCount;
	}

	dispatch(Actions.setLoading(section, true));

	JobService.getAll({
		asc       : false,
		unStaffed : false,
		status    : section === 'activeJobs' ? 1 : 3,
		pageNumber,
		displayCount,
		agencyId,
	})
		.then(({ data }) => {
			dispatch(Actions.receiveSectionData(section, data.ItemList, data.Count));
			dispatch(Actions.setLoading(section, false));
		})
		.catch(apiErrorHandler({
			dispatch,
			module   : 'coordinators',
			property : `loading.${section}`,
		}));
});

const getPosts = (reset) => ((dispatch, getState) => {
	// if the user reloads the page the componentWillUmount will not be called
	// so to reset the activity the reset flag is passed
	const {
		router       : { match : { params : { id } } },
		coordinators : {
			details : {
				activity : {
					data : activityData,
					filters : {
						pagingToken,
						displayCount,
					},
				},
			},
		},
	} = getState();

	dispatch(Actions.setLoading('activity', true));

	CoordinatorService.getPosts({
		id,
		displayCount,
		pagingToken : !reset ? pagingToken : null,
	}).then(({ data }) => {
		const newActivityData = reset ? data.ItemList : activityData.concat(data.ItemList);
		dispatch(Actions.receiveSectionData('activity', newActivityData, data.Count));
		dispatch(Actions.setLoading('activity', false));
		dispatch(Actions.updateSectionFilters('activity', {
			pagingToken : data.PagingToken,
			hasNext     : data.HasNext,
		}));
	}).catch(apiErrorHandler({
		dispatch,
		module   : 'coordinators',
		property : 'loading.activity',
	}));
});

const getLocations = () => ((dispatch, getState) => {
	const {
		match : { params : { id } },
	} = getState().router;
	const { filters : { pageNumber, displayCount } } = getState().coordinators.details.locations;

	dispatch(Actions.setLoading('locations', true));

	CoordinatorService.getLocations({
		pageNumber,
		displayCount,
		id,
	})
		.then(({ data }) => {
			dispatch(Actions.receiveSectionData('locations', data.ItemList, data.Count));
			dispatch(Actions.setLoading('locations', false));
		})
		.catch(apiErrorHandler({
			dispatch,
			module   : 'coordinators',
			property : 'loading.location',
		}));
});

const updateQueryString = (propertyObj) => ((dispatch, getState) => {
	const { router : { location : { search } } } = getState();
	const newQuery = queryString.parse(search);
	forEach(propertyObj, (item, key) => {
		newQuery[key] = item;
	});
	dispatch(push({ pathname : '/coordinators', search : queryString.stringify(newQuery) }));
	dispatch(getCoordinatorsData());
});

const updateCoordinatorsFilter = (obj) => ((dispatch) => {
	dispatch(Actions.updateCoordinatorsFilter(obj));
	dispatch(updateQueryString(obj));
});

const handleQueryStringOnBackButtonClick = (searchString) => ((dispatch) => {
	const updatedQueryString = queryString.parse(searchString);
	if (isEmpty(updatedQueryString)) dispatch(Actions.resetCoordinatorsValues(['filters']));
	else {
		const resetFilterArray = utils.queryStringDiff(updatedQueryString, COORDINATORS_FILTERS);
		dispatch(Actions.resetJobsFilter(resetFilterArray));
	}
	dispatch(getCoordinatorsData());
});

const pendingStatusChange = (item, status) => ((dispatch) => {
	dispatch(Actions.updateStoreProperties({ loading : true }));
	const newItem = cloneDeep(item);
	newItem.Status = status;
	CoordinatorService.pendingStatusChange(newItem)
		.then(() => {
			dispatch(fetchPendingInvitations());
		})
		.catch(apiErrorHandler({
			dispatch,
			module   : 'coordinators',
			property : 'loading.list',
		}));
});

export default {
	getCoordinatorsData,
	updateCoordinatorsFilter,
	handleQueryStringOnBackButtonClick,
	pendingStatusChange,
	getDetailsData,
	getJobsData,
	getPosts,
	getLocations,
};
